import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  api,
  type StatsOverview,
  type StatsDashboard,
  type DashboardAgeGroup,
  type TeamStats,
  type Team,
  type GameSummary,
} from '../services/api';
import { appState } from '../store/AppState';
import './PitcherAvailabilityList';

// How many extra games to request per "Load more" tap. Matches the embedded
// page size the backend ships in /stats/overview and /stats/team/{id}.
const GAMES_PAGE_SIZE = 10;

// Canonical order for the by-age-group section. The backend already returns
// rows in this order, but we re-key to guarantee structure even if a future
// payload drops a bucket.
const AGE_GROUP_ORDER: DashboardAgeGroup[] = ['U11', 'U13', 'U15', 'U18', 'U22', 'Unknown'];

// Duration of the count-up animation for the This Week tile, in ms.
const COUNT_UP_DURATION_MS = 600;

@customElement('stats-view')
export class StatsView extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private stats: StatsOverview | null = null;

  @state()
  private teamStats: TeamStats[] = [];

  @state()
  private selectedTeamIndex = 0;

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private allTeams: Team[] = [];

  @state()
  private directorySearch = '';

  @state()
  private browseTeamStats: TeamStats | null = null;

  @state()
  private browseLoading = false;

  // Pagination state for the three game-history sections.
  //
  // Each section starts with the embedded first page (from /stats/overview
  // or /stats/team/{id}). "Load more" calls /stats/games and appends the
  // returned rows here. We key the per-team maps by team id so flipping
  // between team tabs / browsed teams keeps each list's accumulated state.
  @state()
  private extraTeamGames: Record<string, GameSummary[]> = {};

  @state()
  private loadingMoreTeam: Record<string, boolean> = {};

  @state()
  private extraBrowseGames: Record<string, GameSummary[]> = {};

  @state()
  private loadingMoreBrowse: Record<string, boolean> = {};

  @state()
  private extraOverviewGames: GameSummary[] = [];

  @state()
  private loadingMoreOverview = false;

  // Dashboard payload powering the redesigned league view (This Week,
  // by-age-group, most active teams, top pitchers). Fetched in parallel
  // with /stats/overview — see loadStats().
  @state()
  private dashboard: StatsDashboard | null = null;

  // Animated values for the four This Week tiles. We tween from 0 → target
  // over COUNT_UP_DURATION_MS once the dashboard payload lands. State is
  // an array of four numbers in tile-render order (games, pitches, teams,
  // players) to keep the render path branchless.
  @state()
  private weekDisplay: [number, number, number, number] = [0, 0, 0, 0];

  // Track the active rAF id so we can cancel cleanly on disconnect /
  // re-fetch (e.g. retry after an error).
  private countUpRafId: number | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadStats();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.countUpRafId !== null) {
      cancelAnimationFrame(this.countUpRafId);
      this.countUpRafId = null;
    }
  }

  private get isCoachView(): boolean {
    return appState.isCoach;
  }

  private async loadStats() {
    this.loading = true;
    this.error = null;

    try {
      if (this.isCoachView) {
        // Coach: load team-specific stats for each assigned team
        const teamIds = appState.userTeamIds;
        const results = await Promise.all(
          teamIds.map(id => api.getTeamStats(id))
        );
        this.teamStats = results;
      } else {
        // Admin / unscoped: load league overview + dashboard in parallel.
        // Overview still drives Recent Games + its pagination; dashboard
        // drives the new This Week / age-group / active-teams / pitchers
        // sections that replaced the chart-heavy MVP layout.
        const [overview, dashboard] = await Promise.all([
          api.getStatsOverview(),
          api.getStatsDashboard().catch((err) => {
            // Dashboard is a soft dep — if the new endpoint isn't
            // available yet (parallel rollout), don't fail the whole view.
            console.warn('Dashboard fetch failed (non-fatal):', err);
            return null;
          }),
        ]);
        this.stats = overview;
        this.dashboard = dashboard;
        if (dashboard) {
          this.startCountUp(dashboard.this_week);
        }
        // Public guest: also auto-load the team list so the directory
        // search is immediately usable on first paint (the primary use
        // case for guest mode is "find my team", not browsing league
        // totals).
        if (appState.isPublicMode) {
          void this.loadAllTeams();
        }
      }
    } catch (error) {
      this.error = 'Failed to load statistics';
      console.error('Failed to load stats:', error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Tween the four This Week numbers from 0 → their final values over
   * ~600ms using ease-out. Subtle delight: it draws the eye to the
   * top-of-page summary on first load without being showy. Skipped (jumps
   * straight to final values) when all four are 0 so the empty state
   * doesn't flicker through animation frames.
   */
  private startCountUp(target: { games: number; pitches: number; active_teams: number; active_players: number }) {
    const finals: [number, number, number, number] = [
      target.games,
      target.pitches,
      target.active_teams,
      target.active_players,
    ];
    if (finals.every((n) => n === 0)) {
      this.weekDisplay = finals;
      return;
    }
    if (this.countUpRafId !== null) {
      cancelAnimationFrame(this.countUpRafId);
    }
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / COUNT_UP_DURATION_MS);
      // Ease-out cubic — same curve daisyUI's transitions use.
      const eased = 1 - Math.pow(1 - t, 3);
      this.weekDisplay = [
        Math.round(finals[0] * eased),
        Math.round(finals[1] * eased),
        Math.round(finals[2] * eased),
        Math.round(finals[3] * eased),
      ];
      if (t < 1) {
        this.countUpRafId = requestAnimationFrame(tick);
      } else {
        this.weekDisplay = finals;
        this.countUpRafId = null;
      }
    };
    this.countUpRafId = requestAnimationFrame(tick);
  }

  render() {
    if (this.loading) {
      // Skeleton mirrors the new section order so the layout doesn't jump
      // when data lands: header → This Week 2x2/4-across → age-group rows
      // → active teams list → top pitchers table → recent games.
      return html`
        <div class="space-y-6" data-testid="stats-skeleton">
          <div class="flex items-center gap-3">
            <div class="skeleton w-10 h-10 rounded-lg"></div>
            <div class="skeleton h-8 w-32"></div>
          </div>
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body p-4">
              <div class="skeleton h-4 w-24 mb-3"></div>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                ${[1, 2, 3, 4].map(() => html`<div class="skeleton h-16 rounded-lg"></div>`)}
              </div>
            </div>
          </div>
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <div class="skeleton h-5 w-40 mb-4"></div>
              <div class="space-y-2">
                ${[1, 2, 3, 4, 5, 6].map(() => html`<div class="skeleton h-10 rounded-lg"></div>`)}
              </div>
            </div>
          </div>
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <div class="skeleton h-5 w-40 mb-4"></div>
              <div class="space-y-2">
                ${[1, 2, 3, 4, 5].map(() => html`<div class="skeleton h-14 rounded-lg"></div>`)}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="card bg-error/10 border border-error/20">
          <div class="card-body items-center text-center py-12">
            <div class="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-error">${this.error}</h3>
            <button class="btn btn-error btn-sm mt-4" @click=${this.loadStats}>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              Retry
            </button>
          </div>
        </div>
      `;
    }

    if (this.isCoachView) {
      return this.renderCoachStats();
    }

    return this.renderLeagueStats();
  }

  private renderCoachStats() {
    if (this.teamStats.length === 0) {
      return html`
        <div class="card bg-base-100">
          <div class="card-body items-center text-center py-16">
            <div class="w-20 h-20 bg-base-200 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-base-content/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-base-content/70">No Statistics Available</h3>
            <p class="text-sm text-base-content/50">Start tracking pitches to see your team stats here</p>
          </div>
        </div>
      `;
    }

    const ts = this.teamStats[this.selectedTeamIndex];

    return html`
      <div class="space-y-6 animate-fade-in">
        <!-- Header -->
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-2xl font-bold">Team Statistics</h2>
        </div>

        <!-- Team selector (if multiple teams) -->
        ${this.teamStats.length > 1 ? html`
          <div class="tabs tabs-boxed bg-base-200 w-fit">
            ${this.teamStats.map((t, i) => html`
              <button
                class="tab ${i === this.selectedTeamIndex ? 'tab-active' : ''}"
                @click=${() => { this.selectedTeamIndex = i; }}
              >${t.team.name}</button>
            `)}
          </div>
        ` : nothing}

        <!-- Overview cards -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
          ${this.renderStatCard('Total Pitches', ts.total_pitches, 'pitch')}
          ${this.renderStatCard('Games', ts.total_games, 'game')}
          ${this.renderStatCard('Players', ts.players?.length ?? 0, 'player')}
        </div>

        <!-- Players table -->
        ${ts.players?.length > 0 ? html`
          <div class="card bg-base-100 shadow-sm hover:shadow-md transition-shadow">
            <div class="card-body">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center gap-2">
                  <div class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-accent-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold">Player Pitches</h3>
                </div>
                <span class="badge badge-primary badge-sm">${ts.team.name}</span>
              </div>
              <div class="overflow-x-auto -mx-4 px-4">
                <table class="table table-sm">
                  <thead>
                    <tr class="border-b border-base-200">
                      <th class="bg-transparent">#</th>
                      <th class="bg-transparent">Player</th>
                      <th class="bg-transparent text-right">Pitches</th>
                      <th class="bg-transparent text-right hidden sm:table-cell">Games</th>
                      <th class="bg-transparent text-right">Avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${ts.players.map((player, i) => html`
                      <tr class="hover:bg-base-200/50 transition-colors">
                        <td>
                          <span class="badge ${i < 3 ? 'badge-primary' : 'badge-ghost'} badge-sm font-bold">${i + 1}</span>
                        </td>
                        <td class="font-medium">${player.player_name}</td>
                        <td class="text-right font-mono font-bold text-primary">${player.total_pitches.toLocaleString()}</td>
                        <td class="text-right hidden sm:table-cell">${player.games_played}</td>
                        <td class="text-right font-mono text-base-content/70">${player.avg_per_game.toFixed(1)}</td>
                      </tr>
                    `)}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ` : nothing}

        <!-- Pitcher availability for the coach's own team -->
        ${appState.userTeamIds.includes(ts.team.id) ? html`
          <div class="card bg-base-100 shadow-sm hover:shadow-md transition-shadow" data-testid="pitcher-availability-stats-card">
            <div class="card-body">
              <pitcher-availability-list .teamId=${ts.team.id}></pitcher-availability-list>
            </div>
          </div>
        ` : nothing}

        <!-- Game history -->
        ${(() => {
          const teamId = ts.team.id;
          const extras = this.extraTeamGames[teamId] ?? [];
          const allGames = [...(ts.game_history ?? []), ...extras];
          const total = ts.game_history_total ?? allGames.length;
          if (allGames.length === 0) return nothing;
          return html`
            <div class="card bg-base-100 shadow-sm hover:shadow-md transition-shadow">
              <div class="card-body">
                <div class="flex items-center gap-2 mb-4">
                  <div class="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold">Game History</h3>
                </div>
                <div class="space-y-3">
                  ${allGames.map((game, idx) => html`
                    <button
                      type="button"
                      class="game-history-row w-full text-left flex justify-between items-center p-4 bg-base-200/50 rounded-xl hover:bg-base-200 hover:shadow-md transition-all duration-200 min-h-[64px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${idx >= (ts.game_history?.length ?? 0) ? 'animate-fade-in' : ''}"
                      aria-label=${`Open ${game.game_name} from ${new Date(game.game_date).toLocaleDateString()}`}
                      data-game-id=${game.game_id}
                      @click=${() => this.openGame(game.game_id)}
                    >
                      <div class="min-w-0 flex-1">
                        <div class="font-semibold truncate">${game.game_name}</div>
                        <div class="flex items-center gap-2 text-sm text-base-content/60 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          ${new Date(game.game_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div class="text-right ml-4 flex-shrink-0">
                        <div class="text-2xl font-bold text-primary">${game.total_pitches.toLocaleString()}</div>
                        <div class="text-xs text-base-content/50">${game.player_count} player${game.player_count !== 1 ? 's' : ''}</div>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/40 ml-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  `)}
                </div>
                ${this.renderLoadMore({
                  rendered: allGames.length,
                  total,
                  loading: !!this.loadingMoreTeam[teamId],
                  onClick: () => this.loadMoreTeamGames(teamId, allGames.length),
                })}
              </div>
            </div>
          `;
        })()}

        <!-- Team Directory -->
        ${this.renderTeamDirectory()}
      </div>
    `;
  }

  private async loadAllTeams() {
    if (this.allTeams.length > 0) return;
    try {
      this.allTeams = await api.getTeams();
    } catch (err) {
      console.error('Failed to load teams:', err);
    }
  }

  /**
   * Navigate to the Count tab and load the given game for review.
   *
   * Uses the same URL-driven pattern as shared game links: pushes `?game=<id>`
   * onto history, then switches to the count tab with `updateUrl: false` so
   * the tab switch doesn't clobber the game param. PitchCounter's
   * `connectedCallback` reads `appState.getGameIdFromUrl()` on mount and loads
   * the game via `selectGameFromUrl`. Approved games will lock automatically.
   *
   * In public (guest) mode there is no Count tab — instead we route to the
   * read-only PublicGameView via AppState's public_game URL param, which
   * App.ts renders in place of <stats-view>.
   */
  private openGame(gameId: string) {
    if (appState.isPublicMode) {
      appState.setPublicGameId(gameId);
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('game', gameId);
    url.searchParams.delete('tab'); // 'count' is the default tab, no param needed
    window.history.pushState({ gameId }, '', url.toString());
    appState.setActiveTab('count', { updateUrl: false });
  }

  private async browseTeam(teamId: string) {
    this.browseLoading = true;
    // Reset the browsed-team's "extra games" buffer so switching teams
    // doesn't leak rows from the previously selected team.
    this.extraBrowseGames = { ...this.extraBrowseGames, [teamId]: [] };
    try {
      this.browseTeamStats = await api.getTeamStats(teamId);
    } catch (err) {
      console.error('Failed to load team stats:', err);
    } finally {
      this.browseLoading = false;
    }
  }

  /**
   * Fetch the next page of games for the per-team Game History list.
   * Appends to extraTeamGames[teamId] so the rendered list grows.
   */
  private async loadMoreTeamGames(teamId: string, currentRendered: number) {
    if (this.loadingMoreTeam[teamId]) return;
    this.loadingMoreTeam = { ...this.loadingMoreTeam, [teamId]: true };
    try {
      const page = await api.getPaginatedGames({
        teamId,
        limit: GAMES_PAGE_SIZE,
        offset: currentRendered,
      });
      const prev = this.extraTeamGames[teamId] ?? [];
      // Trigger re-render via fresh object identity (LitElement's @state only
      // diffs by reference for objects/arrays).
      this.extraTeamGames = {
        ...this.extraTeamGames,
        [teamId]: [...prev, ...page.games],
      };
    } catch (err) {
      console.error('Failed to load more games:', err);
    } finally {
      this.loadingMoreTeam = { ...this.loadingMoreTeam, [teamId]: false };
    }
  }

  /**
   * Fetch the next page of games for the browsed-team "Recent Games" section.
   */
  private async loadMoreBrowseGames(teamId: string, currentRendered: number) {
    if (this.loadingMoreBrowse[teamId]) return;
    this.loadingMoreBrowse = { ...this.loadingMoreBrowse, [teamId]: true };
    try {
      const page = await api.getPaginatedGames({
        teamId,
        limit: GAMES_PAGE_SIZE,
        offset: currentRendered,
      });
      const prev = this.extraBrowseGames[teamId] ?? [];
      this.extraBrowseGames = {
        ...this.extraBrowseGames,
        [teamId]: [...prev, ...page.games],
      };
    } catch (err) {
      console.error('Failed to load more games:', err);
    } finally {
      this.loadingMoreBrowse = { ...this.loadingMoreBrowse, [teamId]: false };
    }
  }

  /**
   * Fetch the next page of games for the org-wide "Recent Games" section.
   */
  private async loadMoreOverviewGames(currentRendered: number) {
    if (this.loadingMoreOverview) return;
    this.loadingMoreOverview = true;
    try {
      const page = await api.getPaginatedGames({
        limit: GAMES_PAGE_SIZE,
        offset: currentRendered,
      });
      this.extraOverviewGames = [...this.extraOverviewGames, ...page.games];
    } catch (err) {
      console.error('Failed to load more games:', err);
    } finally {
      this.loadingMoreOverview = false;
    }
  }

  /**
   * Render the small "Load more" affordance. Hidden entirely when the list
   * is fully loaded; replaced by an inline spinner while a fetch is in
   * flight to prevent double-tap.
   */
  private renderLoadMore(opts: {
    rendered: number;
    total: number;
    loading: boolean;
    onClick: () => void;
  }) {
    if (opts.rendered >= opts.total) return nothing;
    return html`
      <div class="flex justify-center pt-3">
        ${opts.loading
          ? html`<span class="loading loading-spinner loading-sm" aria-label="Loading more games"></span>`
          : html`
              <button
                type="button"
                class="btn btn-ghost btn-sm load-more-games"
                ?disabled=${opts.loading}
                @click=${opts.onClick}
              >
                Show ${Math.min(GAMES_PAGE_SIZE, opts.total - opts.rendered)} more
              </button>
            `}
      </div>
    `;
  }

  private getFilteredTeams(): Team[] {
    const ownTeamIds = new Set(appState.userTeamIds);
    const otherTeams = this.allTeams.filter((t) => !ownTeamIds.has(t.id));
    const matching = !this.directorySearch.trim()
      ? otherTeams
      : (() => {
          const q = this.directorySearch.toLowerCase().trim();
          return otherTeams.filter(
            (t) => t.name.toLowerCase().includes(q) || (t.city && t.city.toLowerCase().includes(q))
          );
        })();
    // Cap at 100 to keep the rendered list snappy on mobile. allTeams is
    // already sorted by most-recent game date, so the cap surfaces active
    // teams first; idle teams further down the list are still findable
    // via search (search filters before this slice).
    return matching.slice(0, 100);
  }

  private renderTeamDirectory() {
    return html`
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
              </div>
              <h3 class="font-semibold">Team Directory</h3>
            </div>
            <span class="text-xs text-base-content/50">
              ${appState.isPublicMode ? 'Browse teams and games' : 'Check eligibility for opposing players'}
            </span>
          </div>

          ${this.allTeams.length === 0
            ? html`
                <button
                  class="btn btn-outline btn-sm w-full"
                  @click=${this.loadAllTeams}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                  Browse All Teams
                </button>
              `
            : html`
                <!-- Search -->
                <div class="relative mb-3">
                  <input
                    type="text"
                    class="input input-bordered input-sm w-full pl-9"
                    placeholder="Search teams..."
                    .value=${this.directorySearch}
                    @input=${(e: Event) => this.directorySearch = (e.target as HTMLInputElement).value}
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </div>

                <!-- Team list -->
                <div class="max-h-60 overflow-y-auto space-y-1">
                  ${this.getFilteredTeams().map((team) => html`
                    <button
                      class="btn btn-ghost btn-sm w-full justify-start gap-2 text-left ${this.browseTeamStats?.team.id === team.id ? 'btn-active' : ''}"
                      @click=${() => this.browseTeam(team.id)}
                      ?disabled=${this.browseLoading}
                    >
                      <span class="flex-1 truncate">${team.name}</span>
                      ${team.age_group ? html`<span class="badge badge-info badge-sm whitespace-nowrap">${team.age_group}</span>` : nothing}
                      ${team.city ? html`<span class="text-xs text-base-content/50">${team.city}</span>` : nothing}
                    </button>
                  `)}
                  ${this.getFilteredTeams().length === 0
                    ? html`<p class="text-sm text-base-content/50 text-center py-2">No teams match "${this.directorySearch}"</p>`
                    : nothing}
                </div>

                <!-- Browse loading -->
                ${this.browseLoading
                  ? html`<div class="flex justify-center py-4"><span class="loading loading-spinner loading-sm"></span></div>`
                  : nothing}

                <!-- Browsed team stats -->
                ${this.browseTeamStats && !this.browseLoading
                  ? this.renderBrowsedTeamStats(this.browseTeamStats)
                  : nothing}
              `}
        </div>
      </div>
    `;
  }

  private renderBrowsedTeamStats(ts: TeamStats) {
    return html`
      <div class="mt-4 space-y-4">
        <div class="flex items-center justify-between">
          <h4 class="font-bold text-lg">${ts.team.name}</h4>
          <button class="btn btn-ghost btn-xs" @click=${() => { this.browseTeamStats = null; }}>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Quick stats -->
        <div class="grid grid-cols-3 gap-2">
          <div class="text-center p-2 bg-base-200 rounded-lg">
            <div class="text-xl font-bold tabular-nums">${ts.total_pitches.toLocaleString()}</div>
            <div class="text-xs text-base-content/60">Pitches</div>
          </div>
          <div class="text-center p-2 bg-base-200 rounded-lg">
            <div class="text-xl font-bold tabular-nums">${ts.total_games}</div>
            <div class="text-xs text-base-content/60">Games</div>
          </div>
          <div class="text-center p-2 bg-base-200 rounded-lg">
            <div class="text-xl font-bold tabular-nums">${ts.players?.length ?? 0}</div>
            <div class="text-xs text-base-content/60">Players</div>
          </div>
        </div>

        <!-- Players table -->
        ${ts.players?.length > 0 ? html`
          <div class="overflow-x-auto">
            <table class="table table-xs">
              <thead>
                <tr>
                  <th>Player</th>
                  <th class="text-right">Pitches</th>
                  <th class="text-right">Games</th>
                  <th class="text-right">Avg</th>
                </tr>
              </thead>
              <tbody>
                ${ts.players.map((player) => html`
                  <tr class="hover:bg-base-200/50">
                    <td class="font-medium">${player.player_name}</td>
                    <td class="text-right font-mono font-bold text-primary">${player.total_pitches.toLocaleString()}</td>
                    <td class="text-right">${player.games_played}</td>
                    <td class="text-right font-mono text-base-content/70">${player.avg_per_game.toFixed(1)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        ` : html`<p class="text-sm text-base-content/50 text-center">No player data yet</p>`}

        <!-- Game history -->
        ${(() => {
          const teamId = ts.team.id;
          const extras = this.extraBrowseGames[teamId] ?? [];
          const allGames = [...(ts.game_history ?? []), ...extras];
          const total = ts.game_history_total ?? allGames.length;
          if (allGames.length === 0) return nothing;
          return html`
            <div class="space-y-2">
              <h5 class="text-sm font-semibold">Recent Games</h5>
              ${allGames.map((game, idx) => html`
                <button
                  type="button"
                  class="game-history-row w-full text-left flex justify-between items-center p-3 bg-base-200/50 rounded-lg hover:bg-base-200 hover:shadow-md transition-all duration-200 min-h-[56px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${idx >= (ts.game_history?.length ?? 0) ? 'animate-fade-in' : ''}"
                  aria-label=${`Open ${game.game_name} from ${new Date(game.game_date).toLocaleDateString()}`}
                  data-game-id=${game.game_id}
                  @click=${() => this.openGame(game.game_id)}
                >
                  <div class="min-w-0 flex-1">
                    <div class="text-sm font-medium truncate">${game.game_name}</div>
                    <div class="text-xs text-base-content/50">${new Date(game.game_date).toLocaleDateString()}</div>
                  </div>
                  <div class="text-right ml-3">
                    <div class="font-bold text-primary">${game.total_pitches}</div>
                    <div class="text-xs text-base-content/50">${game.player_count}p</div>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/40 ml-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              `)}
              ${this.renderLoadMore({
                rendered: allGames.length,
                total,
                loading: !!this.loadingMoreBrowse[teamId],
                onClick: () => this.loadMoreBrowseGames(teamId, allGames.length),
              })}
            </div>
          `;
        })()}
      </div>
    `;
  }

  private renderLeagueStats() {
    if (!this.stats) {
      return html`
        <div class="card bg-base-100">
          <div class="card-body items-center text-center py-16">
            <div class="w-20 h-20 bg-base-200 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-base-content/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-base-content/70">No Statistics Available</h3>
            <p class="text-sm text-base-content/50">Start tracking pitches to see your stats here</p>
          </div>
        </div>
      `;
    }

    // Section index drives the staggered fade-in: each section gets an
     // incremental animation-delay so cards land in sequence (0–300ms).
     // We track the index here rather than inline so reordering / adding
     // sections doesn't require touching every delay.
    let sectionIdx = 0;
    const sectionDelay = () => `animation-delay: ${Math.min(sectionIdx++, 6) * 50}ms`;

    return html`
      <div class="space-y-6">
        <!-- Public guest mode: surface the team directory FIRST. The
             primary use case is "find my team," not browsing league
             totals — those follow below. -->
        ${appState.isPublicMode
          ? html`<div class="animate-fade-in" style=${sectionDelay()}>${this.renderTeamDirectory()}</div>`
          : nothing}

        <!-- Header -->
        <div class="flex items-center gap-3 animate-fade-in" style=${sectionDelay()}>
          <div class="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          </div>
          <h2 class="text-2xl font-bold">Statistics</h2>
        </div>

        <!-- 1. This Week summary tile -->
        ${this.dashboard
          ? html`<div class="animate-fade-in" style=${sectionDelay()}>${this.renderThisWeekTile(this.dashboard.this_week)}</div>`
          : nothing}

        <!-- 2. Activity by Age Group -->
        ${this.dashboard
          ? html`<div class="animate-fade-in" style=${sectionDelay()}>${this.renderByAgeGroup(this.dashboard.by_age_group)}</div>`
          : nothing}

        <!-- 3. Most Active Teams -->
        ${this.dashboard
          ? html`<div class="animate-fade-in" style=${sectionDelay()}>${this.renderMostActiveTeams(this.dashboard.most_active_teams)}</div>`
          : nothing}

        <!-- 4. Top Pitchers (last 30 days) -->
        ${this.dashboard
          ? html`<div class="animate-fade-in" style=${sectionDelay()}>${this.renderTopPitchers(this.dashboard.top_pitchers_30d)}</div>`
          : nothing}

        <!-- 5. Recent games (unchanged) -->
        ${(() => {
          const baseGames = this.stats?.recent_games ?? [];
          const extras = this.extraOverviewGames;
          const allGames = [...baseGames, ...extras];
          const total = this.stats?.recent_games_total ?? allGames.length;
          if (allGames.length === 0) return nothing;
          return html`
            <div class="card bg-base-100 shadow-sm hover:shadow-md transition-shadow animate-fade-in" style=${sectionDelay()}>
              <div class="card-body">
                <div class="flex items-center gap-2 mb-4">
                  <div class="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                  </div>
                  <h3 class="font-semibold">Recent Games</h3>
                </div>
                <div class="space-y-3">
                  ${allGames.map((game, idx) => html`
                    <button
                      type="button"
                      class="game-history-row w-full text-left flex justify-between items-center p-4 bg-base-200/50 rounded-xl hover:bg-base-200 hover:shadow-md transition-all duration-200 min-h-[64px] focus:outline-none focus:ring-2 focus:ring-primary/40 ${idx >= baseGames.length ? 'animate-fade-in' : ''}"
                      aria-label=${`Open ${game.game_name} from ${new Date(game.game_date).toLocaleDateString()}`}
                      data-game-id=${game.game_id}
                      @click=${() => this.openGame(game.game_id)}
                    >
                      <div class="min-w-0 flex-1">
                        <div class="font-semibold truncate">${game.game_name}</div>
                        <div class="flex items-center gap-2 text-sm text-base-content/60 mt-1">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                          </svg>
                          ${new Date(game.game_date).toLocaleDateString()}
                          ${game.location ? html`
                            <span class="text-base-content/30">•</span>
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span class="truncate">${game.location}</span>
                          ` : nothing}
                        </div>
                      </div>
                      <div class="text-right ml-4 flex-shrink-0">
                        <div class="text-2xl font-bold text-primary">${game.total_pitches.toLocaleString()}</div>
                        <div class="text-xs text-base-content/50">${game.player_count} player${game.player_count !== 1 ? 's' : ''}</div>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/40 ml-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  `)}
                </div>
                ${this.renderLoadMore({
                  rendered: allGames.length,
                  total,
                  loading: this.loadingMoreOverview,
                  onClick: () => this.loadMoreOverviewGames(allGames.length),
                })}
              </div>
            </div>
          `;
        })()}

      </div>
    `;
  }

  /**
   * "This Week" — 4-number summary tile. Mobile: 2x2 grid; md+: single row
   * of 4. Numbers count up from 0 over ~600ms on first paint. When all
   * four are 0, replaces the tile contents with a friendly empty state so
   * coaches landing on the page mid-week don't see a wall of zeros.
   */
  private renderThisWeekTile(week: {
    games: number;
    pitches: number;
    active_teams: number;
    active_players: number;
  }) {
    const allZero =
      week.games === 0 && week.pitches === 0 && week.active_teams === 0 && week.active_players === 0;

    return html`
      <div class="card bg-base-100 shadow-sm" data-testid="this-week-tile">
        <div class="card-body p-4">
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <h3 class="font-semibold">This Week</h3>
            </div>
            <span class="text-xs text-base-content/50">Last 7 days</span>
          </div>

          ${allZero
            ? html`
                <div class="flex flex-col items-center text-center py-6" data-testid="this-week-empty">
                  <div class="w-12 h-12 bg-base-200 rounded-full flex items-center justify-center mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2"/>
                    </svg>
                  </div>
                  <p class="font-medium text-base-content/70">No games played this week yet</p>
                  <p class="text-xs text-base-content/50 mt-1">Check back after the next game</p>
                </div>
              `
            : html`
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="this-week-grid">
                  ${this.renderWeekStat('Games', this.weekDisplay[0], 'game')}
                  ${this.renderWeekStat('Pitches', this.weekDisplay[1], 'pitch')}
                  ${this.renderWeekStat('Active Teams', this.weekDisplay[2], 'team')}
                  ${this.renderWeekStat('Active Players', this.weekDisplay[3], 'player')}
                </div>
              `}
        </div>
      </div>
    `;
  }

  private renderWeekStat(label: string, value: number, type: 'pitch' | 'game' | 'player' | 'team') {
    const colors = {
      pitch: 'bg-primary/10 text-primary',
      game: 'bg-success/10 text-success',
      player: 'bg-info/10 text-info',
      team: 'bg-secondary/10 text-secondary',
    };
    const icons = {
      pitch: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="2"/></svg>`,
      game: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`,
      player: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>`,
      team: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M9 20H4v-2a3 3 0 015.356-1.857M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`,
    };
    return html`
      <div class="flex items-center gap-3 p-2 rounded-lg" data-testid="this-week-stat">
        <div class="w-8 h-8 ${colors[type]} rounded-lg flex items-center justify-center flex-shrink-0">
          ${icons[type]}
        </div>
        <div class="min-w-0">
          <div class="text-2xl font-bold tabular-nums leading-none">${value.toLocaleString()}</div>
          <div class="text-xs text-base-content/60 mt-1 truncate">${label}</div>
        </div>
      </div>
    `;
  }

  /**
   * Activity by Age Group — horizontal bar rows. Always renders all six
   * canonical buckets (U11–U22 + Unknown) so the layout is predictable
   * even on days when only a handful are active. Buckets with 0 pitches
   * get a faint disabled look but still occupy a row.
   */
  private renderByAgeGroup(rows: Array<{
    age_group: DashboardAgeGroup;
    games: number;
    pitches: number;
    players: number;
  }>) {
    // Re-key by age_group so we render in canonical order even if the
    // backend payload is reordered. Missing buckets fall back to 0s.
    const byKey = new Map(rows.map((r) => [r.age_group, r]));
    const orderedRows = AGE_GROUP_ORDER.map(
      (key) => byKey.get(key) ?? { age_group: key, games: 0, pitches: 0, players: 0 }
    );
    const maxPitches = Math.max(1, ...orderedRows.map((r) => r.pitches));

    return html`
      <div class="card bg-base-100 shadow-sm" data-testid="by-age-group">
        <div class="card-body">
          <div class="flex items-center gap-2 mb-4">
            <div class="w-8 h-8 bg-info/10 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <h3 class="font-semibold">Activity by Age Group</h3>
          </div>

          <div class="space-y-2">
            ${orderedRows.map((row) => {
              const isEmpty = row.pitches === 0 && row.games === 0 && row.players === 0;
              // Cap bar width at 70% of max for the highest-volume bucket —
              // leaves visual headroom for the numeric labels on the right.
              const barPct = isEmpty ? 0 : Math.max(4, Math.round((row.pitches / maxPitches) * 70));
              const isUnknown = row.age_group === 'Unknown';
              const badgeClass = isUnknown ? 'badge-warning' : 'badge-info';
              return html`
                <div
                  class="relative rounded-lg overflow-hidden border border-base-200 ${isEmpty ? 'opacity-50' : ''}"
                  data-testid="age-group-row"
                  data-age-group=${row.age_group}
                >
                  <div
                    class="absolute inset-y-0 left-0 bg-primary/10 transition-all duration-500"
                    style=${`width: ${barPct}%`}
                    aria-hidden="true"
                  ></div>
                  <div class="relative flex items-center gap-3 px-3 py-2.5">
                    <span class="badge ${badgeClass} badge-sm font-semibold w-14 justify-center flex-shrink-0">${row.age_group}</span>
                    <div class="flex-1 grid grid-cols-3 gap-2 text-sm tabular-nums">
                      <div class="text-base-content/70"><span class="font-semibold text-base-content">${row.games}</span> <span class="text-xs">games</span></div>
                      <div class="text-base-content/70"><span class="font-semibold text-base-content">${row.pitches.toLocaleString()}</span> <span class="text-xs">pitches</span></div>
                      <div class="text-base-content/70 text-right md:text-left"><span class="font-semibold text-base-content">${row.players}</span> <span class="text-xs">players</span></div>
                    </div>
                  </div>
                </div>
              `;
            })}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Most Active Teams — top 10 list. Each row is a button that opens the
   * team's stats inline via the existing browseTeam() flow. ≥56px tap
   * target for mobile per the design brief.
   */
  private renderMostActiveTeams(teams: Array<{
    team_id: string;
    team_name: string;
    age_group: string | null;
    games_played: number;
    total_pitches: number;
  }>) {
    if (teams.length === 0) {
      return html`
        <div class="card bg-base-100 shadow-sm" data-testid="most-active-teams">
          <div class="card-body">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h3 class="font-semibold">Most Active Teams</h3>
            </div>
            <p class="text-sm text-base-content/50 text-center py-6">No team activity yet</p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="card bg-base-100 shadow-sm" data-testid="most-active-teams">
        <div class="card-body">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </div>
              <h3 class="font-semibold">Most Active Teams</h3>
            </div>
            <span class="badge badge-primary badge-sm">Top ${Math.min(10, teams.length)}</span>
          </div>

          <div class="space-y-2">
            ${teams.slice(0, 10).map((t, i) => html`
              <button
                type="button"
                class="active-team-row w-full text-left flex items-center gap-3 p-3 rounded-xl bg-base-200/40 hover:bg-base-200 hover:shadow-md transition-all duration-200 min-h-[56px] focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-team-id=${t.team_id}
                aria-label=${`Open ${t.team_name} stats`}
                @click=${() => this.browseTeam(t.team_id)}
              >
                <span class="badge ${i < 3 ? 'badge-primary' : 'badge-ghost'} badge-sm font-bold flex-shrink-0 w-7 justify-center">${i + 1}</span>
                <div class="min-w-0 flex-1 flex items-center gap-2">
                  <span class="font-medium truncate">${t.team_name}</span>
                  ${t.age_group
                    ? html`<span class="badge badge-info badge-sm flex-shrink-0">${t.age_group}</span>`
                    : nothing}
                </div>
                <div class="text-right flex-shrink-0 tabular-nums">
                  <div class="font-bold text-primary">${t.total_pitches.toLocaleString()}</div>
                  <div class="text-xs text-base-content/50">${t.games_played} game${t.games_played !== 1 ? 's' : ''}</div>
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-base-content/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            `)}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Top Pitchers in the last 30 days. Same shape as the legacy "Most
   * Pitches" table but rolling-window, and on small screens it collapses
   * to a stacked layout (name + team on the left, big pitches number on
   * the right). Rows are non-interactive for now — adding "open player
   * stats" requires a parent route that doesn't yet exist on guest mode;
   * leaving as a follow-up.
   */
  private renderTopPitchers(pitchers: Array<{
    player_id: string;
    player_name: string;
    team_name: string;
    total_pitches: number;
    games_played: number;
    avg_per_game: number;
  }>) {
    if (pitchers.length === 0) {
      return html`
        <div class="card bg-base-100 shadow-sm" data-testid="top-pitchers">
          <div class="card-body">
            <div class="flex items-center gap-2 mb-2">
              <div class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-accent-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                </svg>
              </div>
              <h3 class="font-semibold">Top Pitchers</h3>
            </div>
            <p class="text-sm text-base-content/50 text-center py-6">No pitchers in the last 30 days</p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="card bg-base-100 shadow-sm" data-testid="top-pitchers">
        <div class="card-body">
          <div class="flex items-center justify-between mb-1">
            <div class="flex items-center gap-2">
              <div class="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-accent-content" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                </svg>
              </div>
              <h3 class="font-semibold">Top Pitchers</h3>
            </div>
            <span class="badge badge-primary badge-sm">Top ${Math.min(10, pitchers.length)}</span>
          </div>
          <p class="text-xs text-base-content/50 mb-4">Last 30 days</p>

          <!-- Stacked layout for mobile (≤sm); collapses to nothing on md+ -->
          <div class="space-y-2 md:hidden">
            ${pitchers.slice(0, 10).map((p, i) => html`
              <div class="flex items-center gap-3 p-3 rounded-lg bg-base-200/40" data-testid="top-pitcher-row-mobile">
                <span class="badge ${i < 3 ? 'badge-primary' : 'badge-ghost'} badge-sm font-bold flex-shrink-0 w-7 justify-center">${i + 1}</span>
                <div class="min-w-0 flex-1">
                  <div class="font-medium truncate">${p.player_name}</div>
                  <div class="text-xs text-base-content/60 truncate">${p.team_name || '—'}</div>
                </div>
                <div class="text-right tabular-nums flex-shrink-0">
                  <div class="font-bold text-primary text-lg">${p.total_pitches.toLocaleString()}</div>
                  <div class="text-xs text-base-content/50">${p.games_played}g · ${p.avg_per_game.toFixed(1)} avg</div>
                </div>
              </div>
            `)}
          </div>

          <!-- Table layout for md+ -->
          <div class="overflow-x-auto -mx-4 px-4 hidden md:block">
            <table class="table table-sm">
              <thead>
                <tr class="border-b border-base-200">
                  <th class="bg-transparent">#</th>
                  <th class="bg-transparent">Player</th>
                  <th class="bg-transparent">Team</th>
                  <th class="bg-transparent text-right">Pitches</th>
                  <th class="bg-transparent text-right">Games</th>
                  <th class="bg-transparent text-right">Avg</th>
                </tr>
              </thead>
              <tbody>
                ${pitchers.slice(0, 10).map((p, i) => html`
                  <tr class="hover:bg-base-200/50 transition-colors" data-testid="top-pitcher-row">
                    <td>
                      <span class="badge ${i < 3 ? 'badge-primary' : 'badge-ghost'} badge-sm font-bold">${i + 1}</span>
                    </td>
                    <td class="font-medium">${p.player_name}</td>
                    <td class="text-base-content/60">${p.team_name || '—'}</td>
                    <td class="text-right font-mono font-bold text-primary">${p.total_pitches.toLocaleString()}</td>
                    <td class="text-right">${p.games_played}</td>
                    <td class="text-right font-mono text-base-content/70">${p.avg_per_game.toFixed(1)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  private renderStatCard(label: string, value: number, type: 'pitch' | 'game' | 'player' | 'team') {
    const icons = {
      pitch: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="2"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 0v4m0-4h4m-4 0H8"/></svg>`,
      game: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>`,
      player: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>`,
      team: html`<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>`,
    };
    const colors = {
      pitch: 'bg-primary/10 text-primary',
      game: 'bg-success/10 text-success',
      player: 'bg-info/10 text-info',
      team: 'bg-secondary/10 text-secondary',
    };

    return html`
      <div class="card bg-base-100 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5">
        <div class="card-body p-4">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 ${colors[type]} rounded-lg flex items-center justify-center flex-shrink-0">
              ${icons[type]}
            </div>
            <div class="min-w-0">
              <div class="text-2xl font-bold tabular-nums">${value.toLocaleString()}</div>
              <div class="text-xs text-base-content/60 uppercase tracking-wider">${label}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
